import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, Building2, Loader2 } from 'lucide-react';

const FIELDS = [
  { group: 'Identity', items: [
    { key: 'company_name', label: 'Company Name' },
    { key: 'legal_name', label: 'Legal Name' },
    { key: 'gst_no', label: 'GST Number' },
    { key: 'pan', label: 'PAN' },
    { key: 'cin', label: 'CIN' },
    { key: 'logo_url', label: 'Logo URL' },
  ]},
  { group: 'Address', items: [
    { key: 'address_line1', label: 'Address Line 1' },
    { key: 'address_line2', label: 'Address Line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'country', label: 'Country' },
  ]},
  { group: 'Contact', items: [
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'website', label: 'Website' },
  ]},
  { group: 'Bank Details', items: [
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'bank_account', label: 'Account Number' },
    { key: 'ifsc', label: 'IFSC' },
    { key: 'branch', label: 'Branch' },
  ]},
  { group: 'Signatory', items: [
    { key: 'signatory_name', label: 'Authorized Signatory Name' },
    { key: 'signatory_designation', label: 'Designation' },
  ]},
];

export default function CompanyProfile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/settings/company-profile').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/company-profile', data);
      toast.success('Company profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="company-profile-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Company Profile" title="Company Profile" subtitle="Logo, GST, PAN, address, bank and signatory details — used across PO, Invoice and RA Bill print outs." icon={Building2} />

      <div className="space-y-5 mt-6">
        {FIELDS.map((g) => (
          <div key={g.group} className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E] mb-3">{g.group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {g.items.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-semibold text-slate-600 block mb-1">{f.label}</span>
                  <input
                    type="text"
                    value={data[f.key] || ''}
                    onChange={(e) => update(f.key, e.target.value)}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none"
                    data-testid={`cp-${f.key}`}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end sticky bottom-4">
        <button data-testid="cp-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50 shadow-lg">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
